// LanguageContext.js
// This file manages the language state for the entire application

import React, { createContext, useState, useContext, useEffect } from 'react';
import enTranslations from '../translations/en.json';
import mrTranslations from '../translations/mr.json';

// Step 1: Create a Context
// Think of this as creating a "container" that will hold our language data
const LanguageContext = createContext();

// Step 2: Create a Provider Component
// This component will "wrap" our entire app and provide language data to all children
export const LanguageProvider = ({ children }) => {
  // useState Hook: Creates a state variable that can change
  // 'language' = current value, 'setLanguage' = function to change it
  // 'en' = default value (English)
  const [language, setLanguage] = useState('en');

  // useEffect Hook: Runs code when component mounts (loads)
  // This checks if user previously selected a language and loads it
  useEffect(() => {
    const savedLanguage = localStorage.getItem('preferredLanguage');
    if (savedLanguage) {
      setLanguage(savedLanguage);
    }
  }, []); // Empty array [] means "run only once when component loads"

  // Function to toggle between English and Marathi
  const toggleLanguage = () => {
    const newLanguage = language === 'en' ? 'mr' : 'en';
    setLanguage(newLanguage);
    // Save to localStorage so it persists even after page refresh
    localStorage.setItem('preferredLanguage', newLanguage);
  };

  // Get the correct translation object based on current language
  const translations = language === 'en' ? enTranslations : mrTranslations;

  // Value object: This is what we're "providing" to all child components
  const value = {
    language,           // Current language ('en' or 'mr')
    toggleLanguage,     // Function to switch languages
    t: translations     // The translation object (t is short for translations)
  };

  // Return the Provider with our value
  // {children} means "render whatever components are inside this provider"
  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Step 3: Create a custom hook to use the context
// This makes it easier to access language data in any component
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  
  // Error handling: Make sure this hook is used inside LanguageProvider
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  
  return context;
};

/*
HOW TO USE THIS IN COMPONENTS:

import { useLanguage } from '../contexts/LanguageContext';

function MyComponent() {
  const { language, toggleLanguage, t } = useLanguage();
  
  return (
    <div>
      <h1>{t.hero.title}</h1>
      <button onClick={toggleLanguage}>
        Switch to {language === 'en' ? 'मराठी' : 'English'}
      </button>
    </div>
  );
}
*/
